/**
 * @fileoverview Input/output schemas for inspiration-profile-generator-v1.
 *
 * This agent generates a complete PersonDataProfile from a public figure's name,
 * using the model's training knowledge to produce accurate physical attributes,
 * personality traits, and biographical details.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import {
  SHAPE_DEFINITIONS,
  ATHLETICISM_DEFINITIONS,
  SKINTONE_DEFINITIONS,
  CHEST_DEFINITIONS,
  WAIST_DEFINITIONS,
  HIPS_DEFINITIONS,
  EYE_COLOR_OPTIONS,
  HAIR_COLOR_OPTIONS,
  BODY_ARCHETYPE_NAMES,
  CHARACTER_ARCHETYPE_NAMES,
  ETHNICITY_L1_NAMES,
} from '@/domains/characters/constants';

// =============================================================================
// INPUT SCHEMAS
// =============================================================================

/**
 * Simplified input from UI hooks.
 */
export const HookInputSchema = z.object({
  personName: z.string().min(1).describe('The name of a real-world public figure (celebrity, actor, model, etc.) to generate a profile for.'),
});

export type HookInput = z.infer<typeof HookInputSchema>;

/**
 * Full input for the agent flow - extends base schema.
 */
export const InputSchema = BaseFlowInputSchema.extend({
  personName: z.string().min(1).describe(
    'The name of a real-world public figure (celebrity, actor, model, etc.) to generate a profile for.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema for the generated inspiration profile.
 * All physical attribute fields are required since we expect the model to have
 * knowledge of well-known public figures.
 *
 * Uses enum constraints from the canonical constants to ensure generated values
 * are valid PersonProfileSchema values.
 */
export const GeneratedProfileSchema = z.object({
  isValid: z.boolean().describe(
    'True if the person is recognized and a profile can be generated. False if the person is unknown.'
  ),
  error: z.string().optional().describe(
    'Explanation of why the profile could not be generated, if isValid is false.'
  ),
  profile: z.object({
    name: z.string().describe('The person\'s full commonly-known name.'),
    age: z.number().describe('Current or last-known age in years.'),
    gender: z.enum(['Female', 'Male', 'Non-binary']).describe('Gender: Female, Male, or Non-binary.'),
    profession: z.string().describe('Primary profession or occupation.'),
    height: z.number().describe('Height in centimeters.'),
    shape: z.enum(Object.keys(SHAPE_DEFINITIONS) as [string, ...string[]]).describe('Body shape classification.'),
    athleticism: z.enum(Object.keys(ATHLETICISM_DEFINITIONS) as [string, ...string[]]).describe('Athleticism level classification.'),
    chest: z.enum(Object.keys(CHEST_DEFINITIONS) as [string, ...string[]]).describe('Chest width classification.'),
    waist: z.enum(Object.keys(WAIST_DEFINITIONS) as [string, ...string[]]).describe('Waist width classification.'),
    hips: z.enum(Object.keys(HIPS_DEFINITIONS) as [string, ...string[]]).describe('Hip width classification.'),
    bodyDescription: z.string().describe('Detailed prose paragraph describing the body.'),
    personalityDescription: z.array(z.string()).describe('Array of personality trait strings.'),
    faceDescription: z.string().describe('Detailed prose paragraph describing facial features and hair.'),
    skinTone: z.enum(Object.keys(SKINTONE_DEFINITIONS) as [string, ...string[]]).describe('Skin tone classification.'),
    hairColor: z.enum(HAIR_COLOR_OPTIONS as unknown as [string, ...string[]]).describe('Hair color.'),
    eyeColor: z.enum(EYE_COLOR_OPTIONS as unknown as [string, ...string[]]).describe('Eye color.'),
    ethnicities: z.record(z.enum(ETHNICITY_L1_NAMES), z.array(z.string())).describe('Ethnic background(s) as record of L1 group to L2 sub-ethnicities. Keys MUST be from the official L1 list.'),
    nationalities: z.array(z.string()).describe('Nationality/nationalities.'),
    characterArchetype: z.array(z.enum(CHARACTER_ARCHETYPE_NAMES as unknown as [string, ...string[]])).describe('Character archetype(s), 1-3 entries.'),
    bodyArchetype: z.enum(BODY_ARCHETYPE_NAMES as unknown as [string, ...string[]]).describe('Body archetype classification.'),
    personalityDescriptionV2: z.string().describe('A rich prose paragraph (3-6 sentences) describing the person\'s personality, temperament, and behavioral traits. Focuses purely on personality — no physical appearance.'),
  }).optional().describe('The generated profile. Only present if isValid is true.'),
});

export type GeneratedProfile = z.infer<typeof GeneratedProfileSchema>;

export const OutputSchema = GeneratedProfileSchema;
export type Output = GeneratedProfile;
